import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {ArticleEditInfo} from "../model/article-edit-info";
import {MessageService} from "../service/message.service";
import {ContentService} from "../service/content.service";
import {ContentInfo} from "../model/content-info";
import {ArticleService} from "../service/article.service";
import {ActivatedRoute, Router} from "@angular/router";

declare var wysihtml5: any;

// declare var wysihtml5ParserRules: any; // additional rules as js global object

@Component({
  selector: 'app-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.css',
    '../../../../resources/static/article.css',
    '../../../../resources/static/article-editor-defaults.css'],
  encapsulation: ViewEncapsulation.None
})
export class ArticleComponent implements OnInit, OnDestroy {


  private maxAudioSize = 50;
  private maxPictureSize = 10;
  private maxTitleLength = 256;
  private maxArticleTextLength = 8000;

  articleId: string = null;
  submitting = false;
  article: ArticleEditInfo = new ArticleEditInfo(null, "", '', null, null, false);
  audioFile: File = null;
  pictureFile: File = null;
  audioFilename: string;
  pictureFilename: string;
  richEditor: any;
  titleFieldError: string = null;
  richEditorError: string = null;
  pictureFileError: string = null;
  audioFileError: string = null;

  constructor(private messageService: MessageService,
              private articleService: ArticleService,
              private contentService: ContentService,
              private router: Router,
              private route: ActivatedRoute) {
  }

  ngOnInit() {
    this.richEditor = new wysihtml5.Editor('richTextEdit',
      {
        toolbar: 'toolbar',
        parserRules: ArticleComponent.getRules(),
        useLineBreaks: false,
        stylesheets: ["/article.css", "/article-editor-defaults.css"],
        contentEditableMode: true

      });

    wysihtml5.dom.getPastedHtml = function (event) {
      let html;
      if (event.clipboardData) {
        html = event.clipboardData.getData('text/html');
        if (html == null || html == "") {
          html = event.clipboardData.getData('text/plain');
        } else {
          let htmlIdx = html.indexOf("</html>");
          if (htmlIdx != -1) {
            html = html.substring(0, htmlIdx) + "</html>";
          }
        }
      }
      return html;
    };

    let paramMap = this.route.snapshot.paramMap;

    if (paramMap.has("id")) {
      this.articleId = paramMap.get("id");
      this.articleService.getArticleEdit(this.articleId)
        .then(value => {
          this.article = value;
          this.updateByArticle();
        })
        .catch(this.onRejected());
    } else {
      this.updateByArticle();
    }
  }

  private static getRules(): any {
    return {
      "type_definitions": {
        "text_color_object": {
          "styles": {
            "color": true
          }
        },
        "text_fontsize_object": {
          "styles": {
            "font-size": true
          }
        },
      },
      "classes": {
        "wysiwyg-font-size-large": 1,
        "wysiwyg-font-size-small": 1,
        "wysiwyg-font-size-x-small": 1,
        "wysiwyg-font-size-x-large": 1,
        "wysiwyg-font-size-medium": 1,
        "wysiwyg-text-align-left": 1,
        "wysiwyg-text-align-center": 1,
        "wysiwyg-text-align-justify": 1,
        "wysiwyg-text-align-right": 1
      },
      "tags": {
        "br": {
          "add_class": {
            "clear": "clear_br"
          }
        },
        "ul": {},
        "li": {},
        "i": {},
        "b": {},
        "ol": {},
        "hr": 1,
        "p": {
          "add_class": {
            "align": "align_text"
          }
        },
        "span": {
          "keep_styles": {
            "color": 1,
            "fontSize": 1
          },
          "classes": {
            "wysiwyg-font-size-large": 1,
            "wysiwyg-font-size-larger": 1,
            "wysiwyg-font-size-medium": 1,
            "wysiwyg-font-size-small": 1,
            "wysiwyg-font-size-smaller": 1,
            "wysiwyg-font-size-x-large": 1,
            "wysiwyg-font-size-x-small": 1,
            "wysiwyg-font-size-xx-large": 1,
            "wysiwyg-font-size-xx-small": 1
          },
        },
        "div": 1,
        "style": {
          remove: 1
        }
      },
      "text_formatting_object": {
        "classes": {
          "wysiwyg-font-size-large": 1,
          "wysiwyg-font-size-larger": 1,
          "wysiwyg-font-size-medium": 1,
          "wysiwyg-font-size-small": 1,
          "wysiwyg-font-size-smaller": 1,
          "wysiwyg-font-size-x-large": 1,
          "wysiwyg-font-size-x-small": 1,
          "wysiwyg-font-size-xx-large": 1,
          "wysiwyg-font-size-xx-small": 1
        }
      }
    };
  }

  ngOnDestroy() {
    this.richEditor.destroy();
  }

  onAudioFileInputChange(files: FileList) {
    this.audioFile = files.item(0);
    this.audioFilename = this.audioFile.name;
    if (this.audioFile.size > this.maxAudioSize * 1048576) {
      this.audioFileError = `You have exceeded the maximum allowable file size (${this.maxAudioSize} mb).`;
    } else {
      this.audioFileError = null;
    }
  }

  onPictureFileInputChange(files: FileList) {
    this.pictureFile = files.item(0);
    this.pictureFilename = this.pictureFile.name;
    if (this.pictureFile.size > this.maxPictureSize * 1048576) {
      this.pictureFileError = `You have exceeded the maximum allowable file size (${this.maxPictureSize} mb).`;
    } else {
      this.pictureFileError = null;
    }
  }

  onSubmit() {
    if (this.article.title == null || this.article.title.length == 0) {
      this.titleFieldError = "This field is required.";
    } else if (this.article.title.length > this.maxTitleLength) {
      this.titleFieldError = "You have exceeded the input length.";
    } else {
      this.titleFieldError = null;
    }

    const tmp = document.createElement("DIV");
    tmp.innerHTML = this.richEditor.getValue(true);
    const content = tmp.textContent || tmp.innerText || "";

    if (content.length > this.maxArticleTextLength) {
      this.richEditorError = "You have exceeded the input length.";
    } else {
      this.richEditorError = null;
    }

    if (this.titleFieldError != null
      || this.richEditorError != null
      || this.audioFileError != null
      || this.pictureFileError != null) {
      return;
    }

    // this.submitting = true;

    // In case when user has opened arcticle for edit and hasn't modified files
    let audioPromise = Promise.resolve(null);
    if (this.audioFile != null || this.audioFilename == null) {
      audioPromise = this.uploadFile(this.audioFile)
        .then(value => this.article.audio = value);
    }

    let picturePromise = Promise.resolve(null);
    if (this.pictureFile != null || this.pictureFilename == null) {
      picturePromise = this.uploadFile(this.pictureFile)
        .then(value => this.article.picture = value);
    }

    let allPromises = [audioPromise, picturePromise];
    Promise.all(allPromises)
      .then(() => {
        this.saveArticle();
      })
      .catch(this.onRejected());
  }

  private saveArticle() {
    this.article.text = this.richEditor.getValue(false);
    let savePromise: Promise<any>;
    if (this.articleId == null) {
      savePromise = this.articleService.createArticle(this.article)
    } else {
      savePromise = this.articleService.updateArticle(this.articleId, this.article);
    }
    savePromise.then(() => {
      this.submitting = false;
      this.router.navigate(['/articles']);
    }).catch(this.onRejected());
  }

  private onRejected() {
    return err => {
      this.messageService.addError('error occurred', err);
      console.log(err);
    }
  }

  private uploadFile(file): Promise<ContentInfo> {
    if (file == null || file.size === 0) {
      return Promise.resolve(null);
    }
    let formData = new FormData();
    formData.append("file", file.slice(0, file.size, file.type), file.name);
    return this.contentService.upload(formData).toPromise();
  }

  private updateByArticle() {
    if (this.article.audio != null) {
      this.audioFilename = this.article.audio.original_name;
    }
    if (this.article.picture != null) {
      this.pictureFilename = this.article.picture.original_name;
    }
    this.richEditor.setValue(this.article.text);
  }

  removePictureFile() {
    this.article.picture = null;
    this.pictureFilename = null;
    this.pictureFile = null;
    this.pictureFileError = null;
  }

  removeAudioFile() {
    this.article.audio = null;
    this.audioFilename = null;
    this.audioFile = null;
    this.audioFileError = null;
  }
}



// WEBPACK FOOTER //
// /home/developer/Downloads/SleepRestoreBackend/sleep-restore-boot/src/main/ngapp/src/app/article/article.component.ts